import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout, Header } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import {
  getChatChannels, getChatMessages, sendChatMessage, markChatRead,
  uploadChatFile, createChatGroup, searchChatUsers,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  MessageSquare, Send, Users, User, Hash, Paperclip,
  FileText, Plus, Search, Link2, X, Download, Pencil, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

// Vibrant color palette for users
const USER_COLORS = [
  { bg: 'from-rose-500 to-pink-600', text: 'text-white', light: 'bg-rose-500', bubble: 'bg-gradient-to-br from-rose-500 to-pink-500' },
  { bg: 'from-violet-500 to-purple-600', text: 'text-white', light: 'bg-violet-500', bubble: 'bg-gradient-to-br from-violet-500 to-purple-600' },
  { bg: 'from-blue-500 to-cyan-600', text: 'text-white', light: 'bg-blue-500', bubble: 'bg-gradient-to-br from-blue-500 to-cyan-500' },
  { bg: 'from-emerald-500 to-teal-600', text: 'text-white', light: 'bg-emerald-500', bubble: 'bg-gradient-to-br from-emerald-500 to-teal-600' },
  { bg: 'from-amber-500 to-orange-600', text: 'text-white', light: 'bg-amber-500', bubble: 'bg-gradient-to-br from-amber-500 to-orange-500' },
  { bg: 'from-sky-500 to-indigo-600', text: 'text-white', light: 'bg-sky-500', bubble: 'bg-gradient-to-br from-sky-500 to-indigo-500' },
  { bg: 'from-fuchsia-500 to-pink-600', text: 'text-white', light: 'bg-fuchsia-500', bubble: 'bg-gradient-to-br from-fuchsia-500 to-pink-600' },
  { bg: 'from-lime-500 to-green-600', text: 'text-white', light: 'bg-lime-600', bubble: 'bg-gradient-to-br from-lime-500 to-green-600' },
];

const getUserColor = (senderId) => {
  if (!senderId) return USER_COLORS[0];
  let hash = 0;
  for (let i = 0; i < senderId.length; i++) hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
};

const RenderMessageContent = ({ text, fileUrl, fileName, fileType }) => {
  if (fileUrl) {
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${BACKEND}${fileUrl}`;
    if (fileType === 'image') {
      return (
        <div>
          {text && <p className="mb-2">{text}</p>}
          <img src={fullUrl} alt={fileName} className="max-w-xs rounded-lg border border-white/20" data-testid="chat-image-attachment" />
          <p className="text-xs opacity-70 mt-1">{fileName}</p>
        </div>
      );
    }
    return (
      <div>
        {text && <p className="mb-2">{text}</p>}
        <a href={fullUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="chat-file-attachment">
          <FileText className="w-4 h-4" />
          <span className="text-sm font-medium underline">{fileName}</span>
          <Download className="w-3.5 h-3.5" />
        </a>
      </div>
    );
  }
  if (text && URL_REGEX.test(text)) {
    const parts = text.split(URL_REGEX);
    return (
      <p>{parts.map((part, i) =>
        URL_REGEX.test(part) ? (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5 hover:opacity-80">
            {part.length > 50 ? part.substring(0, 50) + '...' : part}<Link2 className="w-3 h-3 inline" />
          </a>
        ) : <span key={i}>{part}</span>
      )}</p>
    );
  }
  return <p>{text}</p>;
};

const Chat = () => {
  const { user, token } = useAuth();
  const { fetchSidebarCounts } = useNotifications() || {};
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState('all-team');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const eventSourceRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // User status
  const [myStatus, setMyStatus] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusDraft, setStatusDraft] = useState('');
  const [userStatuses, setUserStatuses] = useState({});

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  const fetchChannels = useCallback(async () => {
    try { const res = await getChatChannels(); setChannels(res.data); } catch {}
  }, []);

  const fetchMessages = useCallback(async (channel) => {
    try { const res = await getChatMessages(channel); setMessages(res.data); setTimeout(scrollToBottom, 100); } catch {}
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await api.get('/chat/statuses');
      const statusMap = {};
      res.data.forEach(u => { statusMap[u.id] = u.chat_status || ''; });
      setUserStatuses(statusMap);
      if (user?.id && statusMap[user.id]) setMyStatus(statusMap[user.id]);
    } catch {}
  }, [user?.id]);

  useEffect(() => { fetchChannels(); fetchStatuses(); }, [fetchChannels, fetchStatuses]);
  useEffect(() => {
    fetchMessages(activeChannel);
    markChatRead(activeChannel).then(() => { if (fetchSidebarCounts) fetchSidebarCounts(); }).catch(() => {});
  }, [activeChannel, fetchMessages, fetchSidebarCounts]);

  useEffect(() => {
    if (!token) return;
    let retryTimeout = null;
    let es = null;
    const connect = () => {
      const url = `${BACKEND}/api/notifications/stream?token=${token}`;
      es = new EventSource(url);
      eventSourceRef.current = es;
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'chat_message' && data.message) {
            const msg = data.message;
            if (msg.channel === activeChannel) {
              setMessages(prev => {
                // Remove optimistic message and add real one
                const filtered = prev.filter(m => !(m._optimistic && m.sender_id === msg.sender_id && m.text === msg.text));
                if (filtered.some(m => m.id === msg.id)) return filtered;
                return [...filtered, msg];
              });
              setTimeout(scrollToBottom, 20);
              if (msg.sender_id !== user?.id) markChatRead(activeChannel).then(() => { if (fetchSidebarCounts) fetchSidebarCounts(); }).catch(() => {});
            } else {
              setChannels(prev => prev.map(ch => ch.id === msg.channel ? { ...ch, unread: (ch.unread || 0) + 1 } : ch));
            }
          }
        } catch {}
      };
      es.onerror = () => { es.close(); retryTimeout = setTimeout(connect, 3000); };
    };
    connect();
    return () => { if (es) es.close(); if (retryTimeout) clearTimeout(retryTimeout); };
  }, [token, activeChannel, user?.id, fetchSidebarCounts]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    const text = newMessage.trim();
    setNewMessage('');
    
    // Optimistic update - show message immediately for sender
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      text,
      sender_id: user?.id,
      sender_name: user?.full_name,
      channel: activeChannel,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setTimeout(scrollToBottom, 20);
    
    setSending(true);
    try {
      const ch = channels.find(c => c.id === activeChannel);
      await sendChatMessage({ receiver_id: ch?.user_id || null, channel: activeChannel, text });
    } catch { toast.error('Failed to send'); setNewMessage(text); }
    finally { setSending(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large. Max 10MB.'); return; }
    setUploading(true);
    try {
      const res = await uploadChatFile(file);
      const { file_url, file_name, file_type } = res.data;
      const ch = channels.find(c => c.id === activeChannel);
      await sendChatMessage({ receiver_id: ch?.user_id || null, channel: activeChannel, text: newMessage.trim() || '', file_url, file_name, file_type });
      setNewMessage('');
      toast.success('File sent!');
    } catch { toast.error('Failed to upload file'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 1) { setSearchResults([]); return; }
    try { const res = await searchChatUsers(q); setSearchResults(res.data); } catch {}
  };

  const startDm = (userId) => {
    const dmId = [user?.id, userId].sort().join('-');
    setActiveChannel(`dm-${dmId}`);
    fetchChannels();
    setShowSearch(false); setSearchQuery(''); setSearchResults([]);
  };

  const openCreateGroup = async () => {
    try { const res = await searchChatUsers(''); setAllUsers(res.data); } catch {}
    setShowCreateGroup(true);
  };

  const toggleGroupMember = (uid) => setGroupMembers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) { toast.error('Enter a group name'); return; }
    if (groupMembers.length === 0) { toast.error('Select at least one member'); return; }
    try {
      await createChatGroup({ name: groupName.trim(), member_ids: groupMembers });
      toast.success('Group created!');
      setShowCreateGroup(false); setGroupName(''); setGroupMembers([]);
      fetchChannels();
    } catch { toast.error('Failed to create group'); }
  };

  const saveStatus = async () => {
    try {
      await api.put('/chat/status', { status: statusDraft });
      setMyStatus(statusDraft);
      setEditingStatus(false);
      toast.success('Status updated!');
    } catch { toast.error('Failed to update status'); }
  };

  const activeChannelObj = channels.find(c => c.id === activeChannel);
  const channelName = activeChannelObj?.name || 'All Team';

  const formatTime = (dateStr) => new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString();
  };

  const groupedMessages = messages.reduce((groups, msg) => {
    const date = formatDate(msg.created_at);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  const filteredChannels = showSearch && searchQuery
    ? channels.filter(ch => ch.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : channels;

  return (
    <Layout>
      <Header title="Team Chat" />
      <div className="p-4 h-[calc(100vh-4rem)]" data-testid="chat-page">
        <div className="flex h-full rounded-2xl overflow-hidden shadow-xl border border-slate-200/50">

          {/* ===== SIDEBAR ===== */}
          <div className="w-72 flex flex-col bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="chat-channel-list">
            {/* User Status Section */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getUserColor(user?.id).bg} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                  {user?.full_name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
                  {editingStatus ? (
                    <div className="flex items-center gap-1 mt-0.5">
                      <input value={statusDraft} onChange={e => setStatusDraft(e.target.value)} maxLength={100}
                        className="text-xs bg-white/10 text-white/80 rounded px-1.5 py-0.5 w-full border border-white/20 focus:outline-none focus:border-white/40"
                        placeholder="Set your status..." autoFocus onKeyDown={e => { if (e.key === 'Enter') saveStatus(); if (e.key === 'Escape') setEditingStatus(false); }}
                        data-testid="status-input" />
                      <button onClick={saveStatus} className="text-emerald-400 hover:text-emerald-300"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setEditingStatus(false)} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setStatusDraft(myStatus); setEditingStatus(true); }}
                      className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors" data-testid="edit-status-btn">
                      {myStatus ? <span className="truncate">{myStatus}</span> : <span className="italic">Set your status...</span>}
                      <Pencil className="w-2.5 h-2.5 flex-shrink-0" />
                    </button>
                  )}
                </div>
              </div>

              {/* Search & Actions */}
              <div className="flex items-center gap-1.5">
                <button onClick={() => setShowSearch(!showSearch)}
                  className={`flex-1 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all ${showSearch ? 'bg-white/15 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80'}`}
                  data-testid="toggle-search-btn">
                  <Search className="w-3.5 h-3.5" />
                  <span>Search</span>
                </button>
                <button onClick={openCreateGroup}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 text-xs transition-all"
                  data-testid="create-group-btn">
                  <Plus className="w-3.5 h-3.5" /><span>New</span>
                </button>
              </div>

              {showSearch && (
                <div className="relative mt-2">
                  <Input value={searchQuery} onChange={e => handleSearch(e.target.value)} placeholder="Search users..."
                    className="text-xs h-8 pr-7 bg-white/10 border-white/20 text-white placeholder:text-white/40" data-testid="chat-user-search" />
                  {searchQuery && (
                    <button onClick={() => { setSearchQuery(''); setSearchResults([]); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Search Results */}
            {showSearch && searchResults.length > 0 && (
              <div className="border-b border-white/10 bg-white/5">
                <p className="px-4 py-1.5 text-[10px] font-semibold text-white/40 uppercase">Users</p>
                {searchResults.map(u => (
                  <button key={u.id} onClick={() => startDm(u.id)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-white/10 transition-colors" data-testid={`search-result-${u.id}`}>
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getUserColor(u.id).bg} flex items-center justify-center text-white text-xs font-bold`}>
                      {u.full_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-white">{u.full_name}</p>
                      <p className="text-[10px] text-white/40">@{u.username}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Channel List */}
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-0.5">
                {filteredChannels.map(ch => {
                  const color = getUserColor(ch.user_id || ch.id);
                  const status = ch.user_id ? userStatuses[ch.user_id] : '';
                  return (
                    <button key={ch.id}
                      onClick={() => { setActiveChannel(ch.id); setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, unread: 0 } : c)); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
                        activeChannel === ch.id
                          ? 'bg-white/15 shadow-lg shadow-white/5'
                          : 'hover:bg-white/5'
                      }`}
                      data-testid={`chat-channel-${ch.id}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold shadow-md ${
                        ch.type === 'group'
                          ? ch.is_custom ? 'bg-gradient-to-br from-purple-500 to-pink-500' : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                          : `bg-gradient-to-br ${color.bg}`
                      }`}>
                        {ch.type === 'group' ? <Users className="w-4 h-4" /> : ch.name?.[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${activeChannel === ch.id ? 'text-white' : 'text-white/80'}`}>{ch.name}</p>
                        <p className="text-[10px] text-white/40 truncate">
                          {status || (ch.type === 'group' ? (ch.is_custom ? 'Custom Group' : 'Team') : 'Direct Message')}
                        </p>
                      </div>
                      {ch.unread > 0 && (
                        <span className="w-5 h-5 bg-gradient-to-br from-rose-500 to-pink-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 animate-pulse">
                          {ch.unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* ===== CHAT AREA ===== */}
          <div className="flex-1 flex flex-col relative overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 z-0">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #cbd5e1 0.5px, transparent 0.5px)', backgroundSize: '24px 24px', opacity: 0.12}} />
            </div>

            {/* Header */}
            <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl z-10 relative">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md ${
                activeChannelObj?.type === 'group'
                  ? activeChannelObj?.is_custom ? 'bg-gradient-to-br from-purple-500 to-pink-500' : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  : `bg-gradient-to-br ${getUserColor(activeChannelObj?.user_id || activeChannel).bg}`
              }`}>
                {activeChannelObj?.type === 'group' ? <Hash className="w-4 h-4" /> : channelName?.[0]?.toUpperCase()}
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm" data-testid="chat-active-channel-name">{channelName}</h4>
                <p className="text-xs text-slate-500">
                  {activeChannelObj?.user_id && userStatuses[activeChannelObj.user_id]
                    ? userStatuses[activeChannelObj.user_id]
                    : activeChannelObj?.type === 'group'
                      ? activeChannelObj?.is_custom ? 'Custom group' : 'Team-wide channel'
                      : 'Direct message'}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 z-10 relative" data-testid="chat-messages-area">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 shadow-xl shadow-blue-500/20 animate-bounce" style={{animationDuration: '3s'}}>
                    <MessageSquare className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium">No messages yet</p>
                  <p className="text-xs text-slate-400 mt-1">Start the conversation!</p>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([date, msgs]) => (
                  <div key={date}>
                    <div className="flex items-center gap-3 my-5">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
                      <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider bg-white/80 px-3 py-1 rounded-full backdrop-blur-sm">{date}</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
                    </div>
                    {msgs.map((msg, idx) => {
                      const isMe = msg.sender_id === user?.id;
                      const color = getUserColor(msg.sender_id);
                      return (
                        <div key={msg.id} className={`flex mb-3 ${isMe ? 'justify-end' : 'justify-start'} chat-msg-enter`}
                          style={{animationDelay: `${idx * 30}ms`}} data-testid={`chat-msg-${msg.id}`}>
                          {!isMe && (
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${color.bg} flex items-center justify-center text-white text-xs font-bold mr-2 mt-5 flex-shrink-0 shadow-md`}>
                              {msg.sender_name?.[0]?.toUpperCase()}
                            </div>
                          )}
                          <div className="max-w-[65%]">
                            {!isMe && (
                              <p className="text-xs font-semibold mb-1 ml-1" style={{color: color.light.replace('bg-', '')}}>{msg.sender_name}</p>
                            )}
                            <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                              isMe
                                ? `${color.bubble} text-white rounded-br-sm shadow-md`
                                : 'bg-white text-slate-800 rounded-bl-sm border border-slate-100'
                            }`}>
                              <RenderMessageContent text={msg.text} fileUrl={msg.file_url} fileName={msg.file_name} fileType={msg.file_type} />
                            </div>
                            <p className={`text-[10px] text-slate-400 mt-1 ${isMe ? 'text-right mr-1' : 'ml-1'}`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="border-t border-slate-200/80 p-4 bg-white/80 backdrop-blur-xl z-10 relative" data-testid="chat-input-area">
              <div className="flex gap-2 items-center">
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx" data-testid="chat-file-input" />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  className="h-10 w-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-blue-600 transition-all flex-shrink-0"
                  data-testid="chat-attach-btn">
                  {uploading ? <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" /> : <Paperclip className="w-4 h-4" />}
                </button>
                <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyPress={handleKeyPress}
                  placeholder={`Message ${channelName}...`}
                  className="flex-1 h-10 rounded-xl border-slate-200 bg-slate-50 focus:bg-white transition-colors"
                  data-testid="chat-message-input" />
                <button onClick={handleSend} disabled={!newMessage.trim() || sending}
                  className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 shadow-md ${
                    newMessage.trim()
                      ? `bg-gradient-to-br ${getUserColor(user?.id).bg} text-white hover:shadow-lg hover:scale-105`
                      : 'bg-slate-100 text-slate-300 cursor-not-allowed shadow-none'
                  }`}
                  data-testid="chat-send-button">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="max-w-md" data-testid="create-group-dialog">
          <DialogHeader><DialogTitle>Create Group</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="form-label">Group Name</Label>
              <Input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="e.g. Sales Team, Night Shift..." data-testid="group-name-input" />
            </div>
            <div>
              <Label className="form-label">Members</Label>
              <ScrollArea className="h-48 border border-slate-200 rounded-lg">
                <div className="p-2 space-y-1">
                  {allUsers.map(u => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`group-member-${u.id}`}>
                      <Checkbox checked={groupMembers.includes(u.id)} onCheckedChange={() => toggleGroupMember(u.id)} />
                      <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getUserColor(u.id).bg} flex items-center justify-center text-white text-xs font-bold`}>
                        {u.full_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{u.full_name}</p>
                        <p className="text-xs text-slate-400">@{u.username}</p>
                      </div>
                    </label>
                  ))}
                  {allUsers.length === 0 && <p className="text-center text-slate-400 text-sm py-4">No users found</p>}
                </div>
              </ScrollArea>
              {groupMembers.length > 0 && <p className="text-xs text-slate-500 mt-1">{groupMembers.length} member(s) selected</p>}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateGroup(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleCreateGroup} data-testid="save-group-btn">Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        .chat-msg-enter { animation: msgEnter 0.15s ease-out both; }
        @keyframes msgEnter { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </Layout>
  );
};

export default Chat;
